import { useEffect, useRef, useState } from "react";
import { Menu, X, User, ChevronDown } from "lucide-react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { POPULATED_ASSORTIMENT_CATEGORIES } from "@/lib/assortiment-categories";
import { trackNavClickFromEvent, trackCtaClick } from "@/lib/analytics";


import elvionLogo from "@/assets/elvion-logo.png.asset.json";
import { responsiveImage } from "@/lib/responsive-image";
import { UspBar } from "@/components/site/UspBar";

const elvionLogoResponsive = responsiveImage(elvionLogo.url, [160, 240, 320]);

interface TopChild {
  href: string;
  label: string;
  route?: true;
  children?: { href: string; label: string; route?: true }[];
}
interface TopLink {
  href: string;
  label: string;
  route?: true;
  children?: TopChild[];
}

const PRODUCT_INFO_ITEMS: { href: string; label: string; route?: true }[] = [
  { href: "/info/verlichting", label: "Verlichting", route: true },
  { href: "/info/beveiliging", label: "Beveiliging", route: true },
  { href: "/info/laadpalen", label: "Laadpalen", route: true },
  { href: "/info/thuisbatterij", label: "Thuisbatterij", route: true },
  { href: "/info/netwerken", label: "Netwerken", route: true },
];

const TOP_LINKS: TopLink[] = [
  {
    href: "/klantenservice",
    label: "Klantenservice",
    route: true,
  },
];

/**
 * Mobiele header (zichtbaar onder `lg` — kleiner dan 1024px).
 * Pas dit bestand aan voor mobiele wijzigingen zonder de desktop/tablet
 * header te raken.
 */
export function HeaderMobile() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const currentHash = routerState.location.hash;
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [expandedTopLink, setExpandedTopLink] = useState<string | null>(null);
  const { session } = useAuth();

  const close = () => {
    setOpen(false);
    setExpandedTopLink(null);
  };

  // Auto-close on route change + smooth scroll
  useEffect(() => {
    setOpen(false);
    setExpandedTopLink(null);

    if (typeof window === "undefined") return;
    const id = window.requestAnimationFrame(() => {
      const hash = (currentHash || "").replace(/^#/, "");
      if (hash) {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          return;
        }
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [currentPath, currentHash]);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const header = document.getElementById("mobile-header-root");
      if (header && !header.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setExpandedTopLink(null);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const toggleTopLink = (label: string) => {
    setExpandedTopLink((prev) => (prev === label ? null : label));
  };

  return (
    <header
      id="mobile-header-root"
      className="sticky top-0 z-50 w-full bg-white lg:hidden"
    >
      <UspBar variant="mobile" />
      <div className="border-b border-border/60 bg-white">
        <div className="mx-auto flex h-[64px] max-w-7xl items-center justify-between gap-3 px-4 sm:px-6">
          <Link to="/" className="flex items-center">
            <img
              src={elvionLogoResponsive?.src ?? elvionLogo.url}
              srcSet={elvionLogoResponsive?.srcSet}
              sizes="160px"
              alt="Elvion Group Nederland"
              width={1204}
              height={328}
              className="h-9 w-auto"
              decoding="async"
            />
          </Link>
          <div className="flex items-center gap-1">
            <Link
              to={session ? "/account" : "/auth"}
              aria-label={session ? "Mijn account" : "Inloggen"}
              data-tour="account"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground"
            >
              <User className="size-5" />
            </Link>
            <button
              ref={menuButtonRef}
              type="button"
              aria-label={open ? "Menu sluiten" : "Menu openen"}
              aria-expanded={open}
              aria-controls="mobile-nav"
              aria-haspopup="menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-orange"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="size-6" aria-hidden="true" /> : <Menu className="size-6" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div id="mobile-nav" className="border-t border-border bg-background">
          <nav aria-label="Hoofdmenu mobiel" className="mx-auto flex max-w-7xl flex-col px-2 py-2 sm:px-4" onClick={(e) => trackNavClickFromEvent(e, "header_mobile")}>
            <Link
              to="/"
              onClick={close}
              className="flex items-center border-b border-border/60 py-4 pl-3 text-base font-semibold text-foreground transition-colors hover:text-orange active:bg-muted/60"
            >
              Home
            </Link>
            <Link
              to="/advies"
              onClick={close}
              className="flex items-center border-b border-border/60 py-4 pl-3 text-base font-semibold text-foreground transition-colors hover:text-orange active:bg-muted/60"
            >
              Advies
            </Link>
            <Link
              to="/elektrotechniek"
              onClick={close}
              className="flex items-center border-b border-border/60 py-4 pl-3 text-base font-semibold text-foreground transition-colors hover:text-orange active:bg-muted/60"
            >
              Elektrotechniek
            </Link>
            <Link
              to="/beveiligingstechniek"
              onClick={close}
              className="flex items-center border-b border-border/60 py-4 pl-3 text-base font-semibold text-foreground transition-colors hover:text-orange active:bg-muted/60"
            >
              Beveiligingstechniek
            </Link>
            <Link
              to="/netwerktechniek"
              onClick={close}
              className="flex items-center border-b border-border/60 py-4 pl-3 text-base font-semibold text-foreground transition-colors hover:text-orange active:bg-muted/60"
            >
              Netwerktechniek
            </Link>
            <div className="border-b border-border/60">
              <div className="flex items-stretch">
                <Link
                  to="/assortiment"
                  onClick={close}
                  className="flex flex-1 items-center py-4 pl-3 text-base font-semibold text-foreground transition-colors hover:text-orange active:bg-muted/60"
                >
                  Assortiment
                </Link>
                <button
                  type="button"
                  onClick={() => toggleTopLink("assortiment")}
                  className="flex items-center justify-center px-4 text-foreground/60 transition-colors active:bg-muted/60 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-orange"
                  aria-label={expandedTopLink === "assortiment" ? "Assortiment inklappen" : "Assortiment uitklappen"}
                  aria-expanded={expandedTopLink === "assortiment"}
                  aria-controls="mobile-nav-assortiment"
                >
                  <ChevronDown
                    aria-hidden="true"
                    className={`size-5 transition-transform duration-200 ${expandedTopLink === "assortiment" ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
              {expandedTopLink === "assortiment" && (
                <div id="mobile-nav-assortiment" className="flex flex-col bg-muted/30 pb-2">
                  {POPULATED_ASSORTIMENT_CATEGORIES.map((item) => (
                    <Link
                      key={item.slug}
                      to="/assortiment"
                      search={{ categorie: item.slug }}
                      onClick={close}
                      className="py-2.5 pl-6 pr-3 text-[15px] text-foreground/80 hover:text-orange focus:outline-none focus-visible:ring-2 focus-visible:ring-orange"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link
              to="/aanbiedingen"
              onClick={close}
              className="flex items-center gap-2 border-b border-border/60 py-4 pl-3 text-base font-semibold text-foreground transition-colors hover:text-orange active:bg-muted/60"
            >
              Aanbiedingen
              <span className="rounded bg-orange px-1.5 py-0.5 text-[10px] font-bold text-white leading-none">
                Deals
              </span>
            </Link>
            <Link
              to="/offerte-aanvragen"
              onClick={close}
              className="flex items-center border-b border-border/60 py-4 pl-3 text-base font-semibold text-foreground transition-colors hover:text-orange active:bg-muted/60"
            >
              Offerte
            </Link>
            <Link
              to="/klantenservice"
              onClick={(e) => {
                trackCtaClick("Contact", "/klantenservice", "sticky_header_mobile");
                close();
              }}
              className="flex items-center border-b border-border/60 py-4 pl-3 text-base font-semibold text-foreground transition-colors hover:text-orange active:bg-muted/60"
            >
              Contact
            </Link>
            <div className="border-b border-border/60">
              <div className="flex items-stretch">
                <span className="flex flex-1 items-center py-4 pl-3 text-base font-semibold text-foreground">
                  Kennisbank
                </span>
                <button
                  type="button"
                  onClick={() => toggleTopLink("product-info")}
                  className="flex items-center justify-center px-4 text-foreground/60 transition-colors active:bg-muted/60 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-orange"
                  aria-label={expandedTopLink === "product-info" ? "Kennisbank inklappen" : "Kennisbank uitklappen"}
                  aria-expanded={expandedTopLink === "product-info"}
                  aria-controls="mobile-nav-product-info"
                >
                  <ChevronDown
                    aria-hidden="true"
                    className={`size-5 transition-transform duration-200 ${expandedTopLink === "product-info" ? "rotate-180" : ""}`}
                  />
                </button>
              </div>
              {expandedTopLink === "product-info" && (
                <div id="mobile-nav-product-info" className="flex flex-col bg-muted/30 pb-2">
                  {PRODUCT_INFO_ITEMS.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={close}
                      className="py-2.5 pl-6 pr-3 text-[15px] text-foreground/80 hover:text-orange focus:outline-none focus-visible:ring-2 focus-visible:ring-orange"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {TOP_LINKS.map((item) =>
              item.children ? (
                <div key={item.label} className="border-t border-border/60">
                  <div className="flex items-stretch">
                    <Link
                      to={item.href}
                      onClick={close}
                      className="flex flex-1 items-center py-4 pl-3 text-base font-semibold text-foreground transition-colors hover:text-orange active:bg-muted/60"
                    >
                      {item.label}
                    </Link>
                    <button
                      onClick={() => toggleTopLink(item.label)}
                      className="flex items-center justify-center px-4 text-foreground/60 transition-colors active:bg-muted/60 hover:text-foreground"
                      aria-label={expandedTopLink === item.label ? "Inklappen" : "Uitklappen"}
                    >
                      <ChevronDown
                        className={`size-5 transition-transform duration-200 ${expandedTopLink === item.label ? "rotate-180" : ""}`}
                      />
                    </button>
                  </div>
                  {expandedTopLink === item.label && (
                    <div className="flex flex-col bg-muted/30 pb-2">
                      {item.children.map((child) =>
                        child.children ? (
                          <div key={child.label}>
                            <div className="px-3 py-3 pl-6 text-[13px] font-semibold uppercase tracking-wider text-foreground/60">
                              {child.label}
                            </div>
                            {child.children.map((g) => (
                              <Link
                                key={g.label}
                                to={g.href}
                                onClick={close}
                                className="block py-2.5 pl-9 pr-3 text-[15px] text-foreground/70 hover:text-orange"
                              >
                                {g.label}
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <Link
                            key={child.label}
                            to={child.href}
                            onClick={close}
                            className="py-3 pl-6 pr-3 text-[15px] text-foreground/70 hover:text-orange"
                          >
                            {child.label}
                          </Link>
                        ),
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={close}
                  className="block border-t border-border/60 py-4 pl-3 text-base text-foreground/80 hover:text-orange"
                >
                  {item.label}
                </Link>
              ),
            )}
            <div className="mt-3 flex flex-col gap-2 px-1 pb-3">
              <Link
                to={session ? "/account" : "/auth"}
                onClick={close}
                className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium"
              >
                <User className="size-4" />
                {session ? "Mijn account" : "Inloggen"}
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
